/**
 * @file GeneratedContent.tsx
 * @description Component for displaying the generated SEO content.
 * Provides options to download or copy the generated content.
 */

import React, { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { GeneratedContentProps } from "../../types";

import { isDevOrTest } from "../utilities/environment";

import DebugInfo from "./DebugInfo";
import DownloadButton from "./DownloadButton";
import WordPressPublishButton from "./WordPressPublishButton";
import GeneratedContentWrapper from "./GeneratedContentWrapper";
import JsonLdViewer from "./JsonLdViewer";

const GeneratedContent: React.FC<GeneratedContentProps> = ({
  contentId,
  title,
  metaDescription,
  summary,
  content,
  jsonLd,
  debugInfo,
  onDownload,
  onCopy,
  onEdit,
  onRefine,
  onPublishToWordPress,
  isPublishingToWordPress,
  isLoading,
  isRefining,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isJsonLdModalOpen, setIsJsonLdModalOpen] = useState(false);
  const showDebugInfo = isDevOrTest && debugInfo;
  const hasJsonLd = jsonLd && jsonLd.trim().length > 0;

  // Extract Schema.org type from JSON-LD
  const getSchemaType = (jsonLdString: string): string | null => {
    if (!jsonLdString) return null;
    try {
      const parsed = JSON.parse(jsonLdString);
      const type = parsed["@type"];
      if (!type) return null;
      
      // Handle array (can have multiple types)
      if (Array.isArray(type) && type.length > 0) {
        const firstType = type[0];
        // Extract type name from URL if needed (e.g., "https://schema.org/Substance" -> "Substance")
        if (typeof firstType === "string") {
          return firstType.includes("/") 
            ? firstType.split("/").pop() || firstType
            : firstType;
        }
        return String(firstType);
      }
      
      // Handle string (can be URL or plain name)
      if (typeof type === "string") {
        return type.includes("/") 
          ? type.split("/").pop() || type
          : type;
      }
      
      return String(type);
    } catch {
      return null;
    }
  };

  const schemaType = hasJsonLd ? getSchemaType(jsonLd) : null;

  const toolbar = (
    <>
      <button
        onClick={onRefine}
        className="px-6 text-base rounded-full btn btn-outline btn-primary"
        disabled={Boolean(isRefining)}
      >
        {isRefining ? "Raffinamento..." : "Raffina"}
      </button>
      <button
        onClick={onEdit}
        className="px-6 text-base rounded-full btn btn-outline btn-primary"
      >
        Modifica
      </button>
      <button
        onClick={() => onCopy(contentRef.current?.innerHTML || "")}
        className="px-6 text-base rounded-full btn btn-outline btn-primary"
      >
        Copia
      </button>
      <DownloadButton
        onDownload={onDownload}
        disabled={!content || Boolean(isLoading)}
        variant="primary"
      />
      {onPublishToWordPress && (
        <WordPressPublishButton
          onPublish={onPublishToWordPress}
          isLoading={isPublishingToWordPress}
          disabled={!content || Boolean(isLoading)}
          variant="primary"
        />
      )}
    </>
  );

  return (
    <GeneratedContentWrapper toolbar={toolbar}>
      <div ref={contentRef}>
        <h1 className="!text-6xl !leading-tight !mb-[.33em] text-neutral">
          {title}
        </h1>
        {summary && (
          <p className="!text-4xl !leading-tight !mb-[2.5em] text-neutral">
            {summary}
          </p>
        )}
        <div className="!my-[2.5em]">
          <p className="text-neutral">
            <span className="font-bold">Meta Description: </span>
            {metaDescription}
          </p>
          {hasJsonLd && schemaType && (
            <p className="text-neutral">
              <span className="font-bold">Schema.org Type: </span>
              <button
                onClick={() => setIsJsonLdModalOpen(true)}
                className="text-primary hover:underline focus:outline-none"
                aria-label={`Visualizza dati strutturati Schema.org: ${schemaType}`}
              >
                {schemaType}
              </button>
            </p>
          )}
        </div>
        <div className="mb-10 divider"></div>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </div>
      {showDebugInfo && (
        <DebugInfo
          debugInfo={debugInfo}
          contentId={contentId}
          generatedContent={content}
        />
      )}
      {hasJsonLd && (
        <JsonLdViewer
          jsonLd={jsonLd}
          isOpen={isJsonLdModalOpen}
          onClose={() => setIsJsonLdModalOpen(false)}
        />
      )}
    </GeneratedContentWrapper>
  );
};

export default GeneratedContent;
